import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import {
  Radio, Truck, HardHat, LayoutDashboard, Eye, ChevronRight,
  Shield, Zap, BarChart3, Globe, Users, Clock, Check, ArrowRight,
  Star, Mail, FileText, Lock, Award, Fuel, MapPin, MessageSquare
} from "lucide-react";

const FEATURES = [
  { icon: LayoutDashboard, title: "Planner Command", desc: "Real-time fleet oversight with intelligent route planning and driver management across unlimited construction sites." },
  { icon: Truck, title: "Driver Cockpit", desc: "Mobile-first interface for route execution, pre-departure checklists, delivery confirmations and break management." },
  { icon: HardHat, title: "Machinist Portal", desc: "Direct fuel requests from the field with QR scanning, machine status management and request tracking." },
  { icon: Eye, title: "Live Feed", desc: "Real-time activity stream across all nodes. Every delivery, request and status change visible instantly." },
  { icon: Globe, title: "Zero Gate Translation", desc: "17-language support with AI-powered translation. Every UI element translated at dashboard entry for global teams." },
  { icon: Shield, title: "GDPR Compliant", desc: "Minimal data collection, encrypted logging, and transparent privacy policies. Built for European regulations." },
];

const PRICING = [
  { id: "starter", name: "Starter", price: 49, currency: "EUR", period: "/mo", features: ["1 Hub", "3 Drivers", "Basic Analytics", "Email Support", "5 Sites"], highlight: false },
  { id: "professional", name: "Professional", price: 149, currency: "EUR", period: "/mo", features: ["5 Hubs", "15 Drivers", "Full Analytics", "AI Translation", "Priority Support", "25 Sites", "PDF Reports"], highlight: true },
  { id: "enterprise", name: "Enterprise", price: 399, currency: "EUR", period: "/mo", features: ["Unlimited Hubs", "Unlimited Drivers", "Priority Support", "Custom Integrations", "GDPR Pack", "Unlimited Sites", "Dedicated Account Manager", "SLA 99.9%"], highlight: false },
];

export default function LandingPage() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [isCheckingPayment, setIsCheckingPayment] = useState(false);
  const [activePage, setActivePage] = useState("home");
  const pricingRef = useRef(null);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      setPaymentStatus("timeout");
      return;
    }
    setIsCheckingPayment(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/checkout/status/${sessionId}`);
      const data = await res.json();
      if (data.payment_status === "paid") {
        setPaymentStatus("success");
        setIsCheckingPayment(false);
        return;
      } else if (data.status === "expired") {
        setPaymentStatus("expired");
        setIsCheckingPayment(false);
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (e) {
      if (attempts < 4) setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      else { setPaymentStatus("error"); setIsCheckingPayment(false); }
    }
  };

  const handleCheckout = async (packageId) => {
    try {
      const origin = window.location.origin;
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/checkout?package_id=${packageId}&origin_url=${encodeURIComponent(origin)}`, { method: 'POST' });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch (e) {
      console.error("Checkout error:", e);
    }
  };

  if (activePage === "terms") return <LegalPage title="Terms of Service" onBack={() => setActivePage("home")} content={<TermsContent />} />;
  if (activePage === "privacy") return <LegalPage title="Privacy Policy" onBack={() => setActivePage("home")} content={<PrivacyContent />} />;
  if (activePage === "refund") return <LegalPage title="Refund Policy" onBack={() => setActivePage("home")} content={<RefundContent />} />;
  if (activePage === "contact") return <LegalPage title="Contact Us" onBack={() => setActivePage("home")} content={<ContactContent />} />;

  return (
    <div className="min-h-[100dvh] bg-slate-950 text-slate-100" data-testid="landing-page">
      {/* Payment Status Banner */}
      {paymentStatus === "success" && (
        <div className="bg-lime-500 text-slate-950 py-4 px-6 text-center font-bold text-sm uppercase tracking-wider animate-slideInDown" data-testid="payment-success">
          <Check className="w-5 h-5 inline mr-2" /> Payment successful! Welcome to Project Pigeon. Check your email for onboarding details.
        </div>
      )}
      {isCheckingPayment && (
        <div className="bg-amber-500 text-slate-950 py-3 px-6 text-center font-bold text-sm uppercase tracking-wider animate-pulse">
          Verifying payment...
        </div>
      )}

      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-slate-950/90 backdrop-blur-md border-b border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-lime-500 rounded-md flex items-center justify-center shadow-[0_0_20px_rgba(132,204,22,0.3)]">
              <Radio className="w-5 h-5 text-slate-950" strokeWidth={2.5} />
            </div>
            <div>
              <h1 className="text-lg font-black tracking-tighter uppercase">Project Pigeon</h1>
              <p className="text-[9px] font-bold tracking-[0.2em] uppercase text-slate-500">Logistics Command</p>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => pricingRef.current?.scrollIntoView({ behavior: "smooth" })} className="text-xs font-bold text-slate-400 uppercase tracking-wider hover:text-lime-400 transition-colors">Pricing</button>
            <button onClick={() => setActivePage("contact")} className="text-xs font-bold text-slate-400 uppercase tracking-wider hover:text-lime-400 transition-colors">Contact</button>
            <button data-testid="nav-launch-btn" onClick={() => nav("/")} className="btn-industrial px-5 py-2.5 bg-lime-500 text-slate-950 rounded-md text-xs font-black shadow-[0_0_15px_rgba(132,204,22,0.3)]">
              Launch App <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden pt-20 pb-28 px-6">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(132,204,22,0.08)_0%,transparent_60%)]" />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-slate-900 border border-slate-800 rounded-full px-4 py-2 mb-8 animate-fadeInUp" style={{ opacity: 0 }}>
            <Award className="w-4 h-4 text-lime-500" />
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Trusted by logistics teams across Europe</span>
          </div>
          <h2 className="text-5xl md:text-7xl font-black tracking-tighter uppercase leading-[0.9] mb-6 animate-fadeInUp stagger-1" style={{ opacity: 0 }}>
            <span className="text-slate-100">Command Your</span><br />
            <span className="text-lime-500">Logistics Fleet</span>
          </h2>
          <p className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto mb-10 font-medium leading-relaxed animate-fadeInUp stagger-2" style={{ opacity: 0 }}>
            The intelligent supply management platform for construction logistics. Real-time fleet tracking, AI-powered translations, and closed-loop communication between Planners, Drivers, and Machinists.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fadeInUp stagger-3" style={{ opacity: 0 }}>
            <button data-testid="hero-start-btn" onClick={() => nav("/")}
              className="btn-industrial px-8 py-4 bg-lime-500 text-slate-950 rounded-md text-sm font-black shadow-[0_0_30px_rgba(132,204,22,0.3)] hover:shadow-[0_0_40px_rgba(132,204,22,0.4)]">
              Start Free Trial <ArrowRight className="w-5 h-5" />
            </button>
            <button onClick={() => pricingRef.current?.scrollIntoView({ behavior: "smooth" })}
              className="btn-industrial px-8 py-4 bg-slate-900 border border-slate-700 text-slate-300 rounded-md text-sm font-black hover:border-lime-500/40">
              View Pricing
            </button>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-12 border-y border-slate-800/50 bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: "150+", label: "Active Hubs" },
              { value: "1200+", label: "Drivers Managed" },
              { value: "50M+", label: "Liters Tracked" },
              { value: "17", label: "Languages" },
            ].map((stat, i) => (
              <div key={i} className="animate-fadeInUp" style={{ animationDelay: `${i * 0.1}s`, opacity: 0 }}>
                <span className="text-3xl md:text-4xl font-black text-lime-500 font-mono block">{stat.value}</span>
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-2 block">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-black tracking-tighter uppercase text-slate-100 mb-4">Built for the Field</h3>
            <p className="text-slate-500 font-medium max-w-xl mx-auto">Every feature designed for construction logistics teams who need reliability, speed, and clarity.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="group p-8 bg-slate-900 border border-slate-800 rounded-md hover:border-lime-500/30 transition-all animate-fadeInUp" style={{ animationDelay: `${i * 0.08}s`, opacity: 0 }}>
                  <div className="w-14 h-14 bg-lime-500/10 rounded-md flex items-center justify-center mb-5 group-hover:bg-lime-500/20 transition-colors">
                    <Icon className="w-7 h-7 text-lime-500" />
                  </div>
                  <h4 className="text-base font-bold text-slate-100 uppercase tracking-tight mb-2">{f.title}</h4>
                  <p className="text-sm text-slate-500 leading-relaxed font-medium">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section ref={pricingRef} className="py-24 px-6 bg-slate-900/30 border-y border-slate-800/50" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-black tracking-tighter uppercase text-slate-100 mb-4">Simple, Transparent Pricing</h3>
            <p className="text-slate-500 font-medium">No hidden fees. Cancel anytime. 14-day free trial on all plans.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {PRICING.map((plan, i) => (
              <div key={plan.id} data-testid={`price-card-${plan.id}`}
                className={`relative p-8 rounded-md border transition-all animate-fadeInUp ${plan.highlight ? "bg-slate-900 border-lime-500/40 shadow-[0_0_30px_rgba(132,204,22,0.1)]" : "bg-slate-900 border-slate-800 hover:border-slate-600"}`}
                style={{ animationDelay: `${i * 0.1}s`, opacity: 0 }}>
                {plan.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-lime-500 text-slate-950 px-4 py-1 rounded-full text-[10px] font-black uppercase tracking-wider">
                    Most Popular
                  </div>
                )}
                <h4 className="text-lg font-bold text-slate-100 uppercase tracking-tight">{plan.name}</h4>
                <div className="mt-4 mb-6 flex items-end gap-1">
                  <span className="text-4xl font-black text-slate-100 font-mono">{plan.currency === "EUR" ? "\u20AC" : "$"}{plan.price}</span>
                  <span className="text-sm text-slate-500 font-bold mb-1">{plan.period}</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((f, fi) => (
                    <li key={fi} className="flex items-center gap-2 text-sm text-slate-400 font-medium">
                      <Check className="w-4 h-4 text-lime-500 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <button data-testid={`checkout-${plan.id}`} onClick={() => handleCheckout(plan.id)}
                  className={`btn-industrial w-full py-4 rounded-md text-sm font-black ${plan.highlight ? "bg-lime-500 text-slate-950 shadow-[0_0_20px_rgba(132,204,22,0.3)]" : "bg-slate-800 text-slate-200 hover:bg-slate-700"}`}>
                  Get Started <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-24 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <Star className="w-8 h-8 text-amber-500 mx-auto mb-6" />
          <blockquote className="text-2xl font-bold text-slate-200 leading-relaxed mb-6">
            "Project Pigeon transformed our fuel logistics. We went from phone calls and spreadsheets to real-time coordination in a week."
          </blockquote>
          <p className="text-sm font-bold text-slate-500 uppercase tracking-wider">Marc V. / Fleet Manager / Construction NV</p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-lime-500">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-4xl font-black tracking-tighter uppercase text-slate-950 mb-4">Ready to Command Your Fleet?</h3>
          <p className="text-lg text-slate-950/70 font-medium mb-8">Start your 14-day free trial. No credit card required.</p>
          <button data-testid="cta-start-btn" onClick={() => nav("/")}
            className="btn-industrial px-10 py-5 bg-slate-950 text-lime-500 rounded-md text-sm font-black shadow-2xl hover:bg-slate-900">
            Launch Project Pigeon <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 border-t border-slate-800 py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-lime-500 rounded flex items-center justify-center"><Radio className="w-4 h-4 text-slate-950" /></div>
                <span className="text-sm font-black uppercase tracking-tight">Project Pigeon</span>
              </div>
              <p className="text-xs text-slate-600 font-medium leading-relaxed">Intelligent supply management for construction logistics teams.</p>
            </div>
            <div>
              <h5 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-4">Product</h5>
              <ul className="space-y-2">
                <li><button onClick={() => pricingRef.current?.scrollIntoView({behavior:"smooth"})} className="text-xs text-slate-400 hover:text-lime-400 font-medium transition-colors">Pricing</button></li>
                <li><button onClick={() => nav("/")} className="text-xs text-slate-400 hover:text-lime-400 font-medium transition-colors">Demo</button></li>
              </ul>
            </div>
            <div>
              <h5 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-4">Legal</h5>
              <ul className="space-y-2">
                <li><button data-testid="footer-terms" onClick={() => setActivePage("terms")} className="text-xs text-slate-400 hover:text-lime-400 font-medium transition-colors">Terms of Service</button></li>
                <li><button data-testid="footer-privacy" onClick={() => setActivePage("privacy")} className="text-xs text-slate-400 hover:text-lime-400 font-medium transition-colors">Privacy Policy</button></li>
                <li><button data-testid="footer-refund" onClick={() => setActivePage("refund")} className="text-xs text-slate-400 hover:text-lime-400 font-medium transition-colors">Refund Policy</button></li>
              </ul>
            </div>
            <div>
              <h5 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-4">Support</h5>
              <ul className="space-y-2">
                <li><button data-testid="footer-contact" onClick={() => setActivePage("contact")} className="text-xs text-slate-400 hover:text-lime-400 font-medium transition-colors">Contact Us</button></li>
                <li><a href="mailto:support@projectpigeon.app" className="text-xs text-slate-400 hover:text-lime-400 font-medium transition-colors">support@projectpigeon.app</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">&copy; 2026 Project Pigeon. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <Lock className="w-4 h-4 text-slate-600" />
              <span className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">Secured by Stripe</span>
              <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/b/ba/Stripe_Logo%2C_revised_2016.svg/120px-Stripe_Logo%2C_revised_2016.svg.png" alt="Stripe" className="h-5 opacity-40" />
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function LegalPage({ title, onBack, content }) {
  return (
    <div className="min-h-[100dvh] bg-slate-950" data-testid="legal-page">
      <nav className="sticky top-0 z-50 bg-slate-950/90 backdrop-blur-md border-b border-slate-800/50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-lime-500 rounded flex items-center justify-center"><Radio className="w-4 h-4 text-slate-950" /></div>
            <span className="text-sm font-black uppercase tracking-tight">Project Pigeon</span>
          </div>
          <button onClick={onBack} className="text-xs font-bold text-slate-400 uppercase tracking-wider hover:text-lime-400 transition-colors flex items-center gap-1">
            <ChevronRight className="w-3 h-3 rotate-180" /> Back
          </button>
        </div>
      </nav>
      <main className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="text-3xl font-black tracking-tighter uppercase text-slate-100 mb-2">{title}</h1>
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-10">Last updated: January 2026</p>
        <div className="prose prose-sm prose-invert max-w-none text-slate-300 leading-relaxed space-y-6">
          {content}
        </div>
      </main>
    </div>
  );
}

function TermsContent() {
  return (
    <>
      <h2 className="text-lg font-bold text-slate-100 mt-8">1. Agreement to Terms</h2>
      <p>By accessing or using Project Pigeon ("Service"), you agree to be bound by these Terms of Service. If you disagree with any part of the terms, you may not access the Service.</p>
      
      <h2 className="text-lg font-bold text-slate-100 mt-8">2. Description of Service</h2>
      <p>Project Pigeon is a logistics management platform designed for construction and fleet operations. The Service provides real-time fleet tracking, driver management, fuel logistics coordination, multi-language support, and communication tools for Planners, Drivers, and Machinists.</p>
      
      <h2 className="text-lg font-bold text-slate-100 mt-8">3. Subscriptions and Billing</h2>
      <p>The Service is offered on a subscription basis. Prices are listed in EUR and billed monthly. All payments are processed securely through Stripe. Your subscription will automatically renew unless cancelled before the renewal date. You may cancel your subscription at any time through your account settings.</p>
      
      <h2 className="text-lg font-bold text-slate-100 mt-8">4. Free Trial</h2>
      <p>New subscribers may be eligible for a 14-day free trial. No credit card is required for the trial period. At the end of the trial, you must subscribe to continue using the Service.</p>
      
      <h2 className="text-lg font-bold text-slate-100 mt-8">5. Acceptable Use</h2>
      <p>You agree not to use the Service for any unlawful purpose or in violation of these Terms. You are responsible for all activity that occurs under your account. You must not share your login credentials with unauthorized parties.</p>
      
      <h2 className="text-lg font-bold text-slate-100 mt-8">6. Intellectual Property</h2>
      <p>The Service and its original content, features, and functionality are owned by Project Pigeon and are protected by international copyright, trademark, and other intellectual property laws.</p>
      
      <h2 className="text-lg font-bold text-slate-100 mt-8">7. Data Ownership</h2>
      <p>You retain ownership of all data you input into the Service. We do not claim ownership of your logistics data, site information, or operational records.</p>
      
      <h2 className="text-lg font-bold text-slate-100 mt-8">8. Limitation of Liability</h2>
      <p>In no event shall Project Pigeon be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or goodwill, resulting from your use of the Service.</p>
      
      <h2 className="text-lg font-bold text-slate-100 mt-8">9. Termination</h2>
      <p>We may terminate or suspend your account immediately, without prior notice, if you breach these Terms. Upon termination, your right to use the Service will cease immediately.</p>
      
      <h2 className="text-lg font-bold text-slate-100 mt-8">10. Governing Law</h2>
      <p>These Terms shall be governed by the laws of Belgium, without regard to its conflict of law provisions. Any disputes will be resolved in the courts of Belgium.</p>
      
      <h2 className="text-lg font-bold text-slate-100 mt-8">11. Contact</h2>
      <p>For questions about these Terms, contact us at: <a href="mailto:legal@projectpigeon.app" className="text-lime-400">legal@projectpigeon.app</a></p>
    </>
  );
}

function PrivacyContent() {
  return (
    <>
      <h2 className="text-lg font-bold text-slate-100 mt-8">1. Information We Collect</h2>
      <p>We collect only the minimum data necessary to provide the Service, in compliance with GDPR and Belgian privacy regulations:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Account information: name, email address, company name</li>
        <li>Operational data: site names, machine identifiers, delivery records, driver names</li>
        <li>Usage data: login timestamps, feature usage (anonymized)</li>
        <li>Payment data: processed and stored exclusively by Stripe (we never store card details)</li>
      </ul>
      
      <h2 className="text-lg font-bold text-slate-100 mt-8">2. How We Use Your Data</h2>
      <p>Your data is used exclusively to:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Provide and maintain the Service</li>
        <li>Process transactions and send billing notifications</li>
        <li>Improve the Service through anonymized usage analytics</li>
        <li>Communicate important updates about the Service</li>
      </ul>
      
      <h2 className="text-lg font-bold text-slate-100 mt-8">3. Data Storage and Security</h2>
      <p>All data is stored on secure servers within the European Union. We use industry-standard encryption (TLS 1.3) for data in transit and AES-256 encryption for data at rest. Access to production data is restricted to authorized personnel only.</p>
      
      <h2 className="text-lg font-bold text-slate-100 mt-8">4. Data Retention</h2>
      <p>We retain your data for the duration of your subscription plus 30 days. After account deletion, all personal data is permanently removed within 30 days. Anonymized operational data may be retained for analytics purposes.</p>
      
      <h2 className="text-lg font-bold text-slate-100 mt-8">5. Your Rights (GDPR)</h2>
      <p>Under GDPR, you have the right to:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Access your personal data</li>
        <li>Rectify inaccurate data</li>
        <li>Request deletion of your data</li>
        <li>Export your data in a portable format</li>
        <li>Object to processing of your data</li>
        <li>Withdraw consent at any time</li>
      </ul>
      
      <h2 className="text-lg font-bold text-slate-100 mt-8">6. Third-Party Services</h2>
      <p>We use the following third-party services:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Stripe</strong>: Payment processing (PCI DSS Level 1 compliant)</li>
        <li><strong>AI Translation</strong>: Text is processed for translation purposes only and not stored by the AI provider</li>
      </ul>
      
      <h2 className="text-lg font-bold text-slate-100 mt-8">7. Cookies</h2>
      <p>We use only essential cookies required for the Service to function. We do not use tracking cookies or third-party advertising cookies.</p>
      
      <h2 className="text-lg font-bold text-slate-100 mt-8">8. Data Protection Officer</h2>
      <p>For privacy inquiries, contact our DPO at: <a href="mailto:privacy@projectpigeon.app" className="text-lime-400">privacy@projectpigeon.app</a></p>
    </>
  );
}

function RefundContent() {
  return (
    <>
      <h2 className="text-lg font-bold text-slate-100 mt-8">Refund Policy</h2>
      <p>We want you to be completely satisfied with Project Pigeon. Here is our refund policy:</p>
      
      <h2 className="text-lg font-bold text-slate-100 mt-8">14-Day Free Trial</h2>
      <p>All new subscriptions include a 14-day free trial. No payment is required during the trial period. If you cancel before the trial ends, you will not be charged.</p>
      
      <h2 className="text-lg font-bold text-slate-100 mt-8">Monthly Subscriptions</h2>
      <p>For monthly subscriptions, you may request a full refund within 7 days of your payment date if you are not satisfied with the Service. After 7 days, refunds are issued on a prorated basis for the remaining days in the billing period.</p>
      
      <h2 className="text-lg font-bold text-slate-100 mt-8">How to Request a Refund</h2>
      <p>To request a refund, contact our support team at <a href="mailto:billing@projectpigeon.app" className="text-lime-400">billing@projectpigeon.app</a> with your account email and reason for the refund. Refunds are processed within 5-10 business days back to the original payment method.</p>
      
      <h2 className="text-lg font-bold text-slate-100 mt-8">Cancellation</h2>
      <p>You may cancel your subscription at any time. After cancellation, you retain access to the Service until the end of your current billing period. No further charges will be made after cancellation.</p>
      
      <h2 className="text-lg font-bold text-slate-100 mt-8">Exceptions</h2>
      <p>Refunds may not be available in cases of:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Violation of our Terms of Service</li>
        <li>Fraudulent activity</li>
        <li>Subscriptions active for more than 30 days without prior refund request</li>
      </ul>
    </>
  );
}

function ContactContent() {
  return (
    <>
      <h2 className="text-lg font-bold text-slate-100 mt-8">Get in Touch</h2>
      <p>We'd love to hear from you. Whether you have a question about features, pricing, or anything else, our team is ready to answer.</p>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
        <div className="p-6 bg-slate-900 border border-slate-800 rounded-md">
          <Mail className="w-6 h-6 text-lime-500 mb-3" />
          <h3 className="text-sm font-bold text-slate-100 uppercase tracking-tight mb-2">General Inquiries</h3>
          <a href="mailto:info@projectpigeon.app" className="text-sm text-lime-400 font-medium">info@projectpigeon.app</a>
        </div>
        <div className="p-6 bg-slate-900 border border-slate-800 rounded-md">
          <FileText className="w-6 h-6 text-lime-500 mb-3" />
          <h3 className="text-sm font-bold text-slate-100 uppercase tracking-tight mb-2">Billing & Payments</h3>
          <a href="mailto:billing@projectpigeon.app" className="text-sm text-lime-400 font-medium">billing@projectpigeon.app</a>
        </div>
        <div className="p-6 bg-slate-900 border border-slate-800 rounded-md">
          <MessageSquare className="w-6 h-6 text-lime-500 mb-3" />
          <h3 className="text-sm font-bold text-slate-100 uppercase tracking-tight mb-2">Technical Support</h3>
          <a href="mailto:support@projectpigeon.app" className="text-sm text-lime-400 font-medium">support@projectpigeon.app</a>
        </div>
        <div className="p-6 bg-slate-900 border border-slate-800 rounded-md">
          <Shield className="w-6 h-6 text-lime-500 mb-3" />
          <h3 className="text-sm font-bold text-slate-100 uppercase tracking-tight mb-2">Privacy & GDPR</h3>
          <a href="mailto:privacy@projectpigeon.app" className="text-sm text-lime-400 font-medium">privacy@projectpigeon.app</a>
        </div>
      </div>
      
      <h2 className="text-lg font-bold text-slate-100 mt-10">Office Address</h2>
      <p className="text-slate-400">
        Project Pigeon<br />
        Logistics Command Center<br />
        Belgium, EU
      </p>
      
      <h2 className="text-lg font-bold text-slate-100 mt-8">Business Hours</h2>
      <p className="text-slate-400">Monday - Friday: 09:00 - 18:00 CET<br />Weekend: Emergency support only</p>
    </>
  );
}
